// src/pages/StudentPuzzles.js
import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, doc, setDoc, getDocs, query, where } from "firebase/firestore";
import { useAuth } from "../AuthContext";
import { Chess } from "chess.js";
import { Chessboard } from "react-chessboard";
import "./StudentPuzzles.css";

const CORRECT_MSGS = ["Correct! 🎉","Amazing! ⭐","Mindblowing! 🤯","Brilliant! 🔥","Outstanding! 💪","Superb! 🌟","Excellent! 👏","Perfect! 🎯","Fantastic! 🚀","You nailed it! 💡"];
const WRONG_MSGS = ["Well tried!","Good attempt!","Keep going!","Almost there!","Nice try!","You are learning!","Stay focused!","Keep it up!","You will get the next one!","Great effort!"];
function getRandom(arr) { return arr[Math.floor(Math.random() * arr.length)]; }
function getStars(score, total) {
  if (total === 0) return 0;
  const pct = score / total;
  if (pct === 1) return 5;
  if (pct >= 0.75) return 3;
  if (pct >= 0.5) return 2;
  if (pct >= 0.25) return 1;
  return 0;
}
function StarDisplay({ stars }) {
  return (
    <div className="star-display">
      {[1,2,3,4,5].map(i => (
        <span key={i} className={`star ${i <= stars ? "filled" : ""} ${stars === 5 && i === 5 ? "gold" : ""}`}>★</span>
      ))}
    </div>
  );
}

export default function StudentPuzzles() {
  const { user } = useAuth();
  const [puzzleSets, setPuzzleSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSet, setActiveSet] = useState(null);
  const [activePuzzleIdx, setActivePuzzleIdx] = useState(0);
  const [myProgress, setMyProgress] = useState({});
  const [solving, setSolving] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);

  // Solving state
  const [currentFen, setCurrentFen] = useState("");
  const [playerMoveIdx, setPlayerMoveIdx] = useState(0);
  const [attempted, setAttempted] = useState(false);
  const [resultMsg, setResultMsg] = useState("");
  const [isCorrect, setIsCorrect] = useState(false);
  const [allMoveFens, setAllMoveFens] = useState([]);
  const [navIdx, setNavIdx] = useState(0);

  // Review state
  const [reviewPuzzleIdx, setReviewPuzzleIdx] = useState(0);
  const [reviewNavIdx, setReviewNavIdx] = useState(0);
  const [reviewMoveFens, setReviewMoveFens] = useState([]);

  const [selectedSquare, setSelectedSquare] = useState(null);

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(collection(db, "puzzleSets"), async snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const mine = all.filter(s => s.studentIds?.includes(user.uid));
      setPuzzleSets(mine);
      // Load this student's own progress docs (puzzleProgress/{setId}__{uid})
      const prog = {};
      try {
        const progSnap = await getDocs(
          query(collection(db, "puzzleProgress"), where("studentId", "==", user.uid))
        );
        progSnap.forEach(d => {
          const data = d.data();
          if (data.setId && data.attempts) prog[data.setId] = data.attempts;
        });
      } catch (e) {
        console.error("Could not load puzzle progress:", e);
      }
      setMyProgress(prog);
      setLoading(false);
    });
    return () => unsub();
  }, [user]);

  function getSetStatus(set) {
    const prog = myProgress[set.id] || {};
    const att = Object.keys(prog).length;
    if (att === 0) return "new";
    if (att < set.totalPuzzles) return "incomplete";
    return "complete";
  }
  function getSetScore(set) {
    return Object.values(myProgress[set.id] || {}).filter(a => a.correct).length;
  }

  function openSet(set) {
    const prog = myProgress[set.id] || {};
    const total = set.totalPuzzles;
    // Count distinct puzzle indices the student has already attempted.
    // Firestore object keys come back as strings, so normalise to numbers.
    const attemptedIdx = new Set(Object.keys(prog).map(k => parseInt(k, 10)));
    const attCount = attemptedIdx.size;

    if (attCount >= total) {
      setActiveSet(set); setReviewMode(true);
      setReviewPuzzleIdx(0); setSolving(true);
      loadReviewPuzzle(set, 0, prog);
      return;
    }
    // Resume at the first puzzle they have NOT attempted yet.
    let startIdx = 0;
    for (let i = 0; i < total; i++) {
      if (!attemptedIdx.has(i)) { startIdx = i; break; }
    }
    setActiveSet(set); setActivePuzzleIdx(startIdx);
    setReviewMode(false); setSolving(true);
    loadPuzzle(set.puzzles[startIdx]);
  }

  function loadPuzzle(puzzle) {
    // Play any pre-moves (opponent moves before student gets control)
    const preMoves = puzzle.preMoves || [];
    let startFen = puzzle.fen;
    const fenHistory = [puzzle.fen];

    if (preMoves.length > 0) {
      try {
        const g = new Chess(puzzle.fen);
        for (const pm of preMoves) {
          const move = g.move({ from: pm.from, to: pm.to, promotion: "q" });
          if (!move) break;
          fenHistory.push(g.fen());
        }
        startFen = g.fen();
      } catch(e) { console.log("Pre-move error:", e); }
    }

    setCurrentFen(startFen);
    setPlayerMoveIdx(0);
    setAttempted(false);
    setResultMsg("");
    setIsCorrect(false);
    setAllMoveFens(fenHistory);
    setNavIdx(fenHistory.length - 1);
    setSelectedSquare(null);
  }

  function loadReviewPuzzle(set, idx, prog) {
    const puzzle = set.puzzles[idx];
    const attempt = (prog || myProgress[set.id] || {})[idx];
    const preMoves = puzzle.preMoves || [];
    const fens = [puzzle.fen];
    const g = new Chess(puzzle.fen);
    // Play pre-moves first
    preMoves.forEach(m => {
      try { g.move({ from: m.from, to: m.to, promotion: "q" }); fens.push(g.fen()); } catch {}
    });
    // Then student's attempt
    (attempt?.moves || []).forEach(m => {
      try { g.move({ from: m.from, to: m.to, promotion: "q" }); fens.push(g.fen()); } catch {}
    });
    setReviewMoveFens(fens);
    setReviewNavIdx(0);
  }

  function onPieceClick(piece, square) {
    if (attempted || reviewMode) return;
    setSelectedSquare(square);
  }

  function onSquareClick(square) {
    if (attempted || reviewMode) return;
    if (square === selectedSquare) { setSelectedSquare(null); return; }
    if (selectedSquare) {
      tryMove(selectedSquare, square);
      setSelectedSquare(null);
      return;
    }
    const piece = getPieceAt(currentFen, square);
    if (piece) setSelectedSquare(square);
  }

  function getPieceAt(fen, square) {
    try {
      const rows = fen.split(" ")[0].split("/");
      const col = square.charCodeAt(0) - 97;
      const row = 8 - parseInt(square[1]);
      let c = 0;
      for (const ch of rows[row]) {
        if (isNaN(ch)) { if (c === col) return { color: ch === ch.toUpperCase() ? "w" : "b", type: ch.toLowerCase() }; c++; }
        else { c += parseInt(ch); }
      }
    } catch {}
    return null;
  }

  function tryMove(src, tgt) {
    try {
      const g = new Chess(currentFen);
      const move = g.move({ from: src, to: tgt, promotion: "q" });
      if (!move) return false;
      const newFen = g.fen();
      setCurrentFen(newFen);
      setSelectedSquare(null);
      handlePlayerMove(src, tgt, move.san, g, newFen);
      return true;
    } catch { return false; }
  }

  function onDrop(src, tgt) {
    if (attempted) return false;
    try {
      const g = new Chess(currentFen);
      const move = g.move({ from: src, to: tgt, promotion: "q" });
      if (!move) return false;
      const newFen = g.fen();
      setCurrentFen(newFen);
      setSelectedSquare(null);
      handlePlayerMove(src, tgt, move.san, g, newFen);
      return true;
    } catch { return false; }
  }

  function handlePlayerMove(src, tgt, san, gameAfterMove, precomputedFen) {
    const puzzle = activeSet.puzzles[activePuzzleIdx];
    const expectedMove = puzzle.solution[playerMoveIdx];
    const isRight = src === expectedMove.from && tgt === expectedMove.to;
    const newFen = precomputedFen || gameAfterMove.fen();
    const newFens = [...allMoveFens, newFen];

    if (!isRight) {
      setAllMoveFens(newFens);
      setNavIdx(newFens.length - 1);
      setAttempted(true);
      setIsCorrect(false);
      setResultMsg(getRandom(WRONG_MSGS));
      saveAttempt(false, [{ from: src, to: tgt, san }]);
      return;
    }

    const nextPlayerMoveIdx = playerMoveIdx + 2;
    const isLastMove = playerMoveIdx >= puzzle.solution.length - 1;

    if (isLastMove || puzzle.solution.length === 1) {
      setAllMoveFens(newFens);
      setNavIdx(newFens.length - 1);
      setAttempted(true);
      setIsCorrect(true);
      setResultMsg(getRandom(CORRECT_MSGS));
      saveAttempt(true, puzzle.solution.slice(0, playerMoveIdx + 1));
      return;
    }

    const opponentMove = puzzle.solution[playerMoveIdx + 1];
    setAllMoveFens(newFens);
    setNavIdx(newFens.length - 1);

    if (opponentMove) {
      setTimeout(() => {
        try {
          const g2 = new Chess(newFen);
          const oMove = g2.move({ from: opponentMove.from, to: opponentMove.to, promotion: "q" });
          if (!oMove) {
            setAttempted(true); setIsCorrect(true);
            setResultMsg(getRandom(CORRECT_MSGS));
            saveAttempt(true, puzzle.solution.slice(0, playerMoveIdx + 1));
            return;
          }
          const fenAfterOpponent = g2.fen();
          const newFens2 = [...newFens, fenAfterOpponent];
          setAllMoveFens(newFens2);
          setNavIdx(newFens2.length - 1);
          setCurrentFen(fenAfterOpponent);
          setPlayerMoveIdx(nextPlayerMoveIdx);
          if (nextPlayerMoveIdx >= puzzle.solution.length) {
            setAttempted(true); setIsCorrect(true);
            setResultMsg(getRandom(CORRECT_MSGS));
            saveAttempt(true, puzzle.solution);
          }
        } catch {
          setAttempted(true); setIsCorrect(true);
          setResultMsg(getRandom(CORRECT_MSGS));
          saveAttempt(true, puzzle.solution.slice(0, playerMoveIdx + 1));
        }
      }, 800);
    } else {
      setAttempted(true); setIsCorrect(true);
      setResultMsg(getRandom(CORRECT_MSGS));
      saveAttempt(true, puzzle.solution.slice(0, playerMoveIdx + 1));
    }
  }

  async function saveAttempt(correct, moves) {
    // Students cannot write to the shared puzzleSets doc (teacher-owned).
    // Each student writes to their OWN progress doc: puzzleProgress/{setId}__{uid}
    const progRef = doc(db, "puzzleProgress", `${activeSet.id}__${user.uid}`);
    try {
      await setDoc(progRef, {
        setId: activeSet.id,
        studentId: user.uid,
        attempts: { [activePuzzleIdx]: { correct, moves } },
        updatedAt: Date.now(),
      }, { merge: true }); // merge so each puzzle adds to the map without wiping the rest
    } catch (e) {
      console.error("Could not save puzzle progress:", e);
    }
    setMyProgress(prev => ({
      ...prev,
      [activeSet.id]: { ...(prev[activeSet.id] || {}), [activePuzzleIdx]: { correct, moves } }
    }));
  }

  function nextPuzzle() {
    const next = activePuzzleIdx + 1;
    if (next >= activeSet.totalPuzzles) { setSolving(false); return; }
    setActivePuzzleIdx(next);
    loadPuzzle(activeSet.puzzles[next]);
  }

  function getDisplayFen() {
    return allMoveFens[navIdx] ?? currentFen;
  }

  if (solving && activeSet) {
    const puzzle = reviewMode ? activeSet.puzzles[reviewPuzzleIdx] : activeSet.puzzles[activePuzzleIdx];
    const prog = myProgress[activeSet.id] || {};
    const reviewAttempt = reviewMode ? prog[reviewPuzzleIdx] : null;
    const displayFen = reviewMode
      ? (reviewMoveFens[reviewNavIdx] || puzzle.fen)
      : getDisplayFen();

    return (
      <div className="puzzle-solver">
        <div className="solver-header">
          <button className="btn btn-secondary" onClick={() => { setSolving(false); setActiveSet(null); }}>← Back</button>
          <div className="solver-title">
            <h3>{activeSet.name}</h3>
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
              {reviewMode
                ? `Review ${reviewPuzzleIdx + 1}/${activeSet.totalPuzzles}`
                : `Puzzle ${activePuzzleIdx + 1} of ${activeSet.totalPuzzles}`}
            </span>
          </div>
        </div>

        <div className="solver-body">
          <div className="solver-board-col">
            <div className="color-to-move">
  {(() => {
                const preMoves = puzzle.preMoves || [];
                if (preMoves.length > 0) {
                  // Student moves as colorToMove, but opponent pre-moved first
                  return puzzle.colorToMove === "w" ? "♔ Find White's best move" : "♚ Find Black's best move";
                }
                return puzzle.colorToMove === "w" ? "♔ White to move" : "♚ Black to move";
              })()}
            </div>
            <div className="solver-board">
              <Chessboard
                position={displayFen}
                onPieceDrop={reviewMode ? () => false : onDrop}
                onPieceClick={reviewMode ? undefined : onPieceClick}
                onSquareClick={reviewMode ? undefined : onSquareClick}
                boardOrientation={puzzle.colorToMove === "w" ? "white" : "black"}
                arePiecesDraggable={!attempted && !reviewMode}
                animateOnSetPosition={true}
                customSquareStyles={(() => {
                  if (!selectedSquare || attempted || reviewMode) return {};
                  try {
                    const g = new Chess(currentFen);
                    const moves = g.moves({ square: selectedSquare, verbose: true });
                    const h = {};
                    h[selectedSquare] = { background: "rgba(255,215,0,0.3)", borderRadius: "4px" };
                    moves.forEach(m => {
                      h[m.to] = g.get(m.to) ? {
                        background: "radial-gradient(circle, transparent 56%, rgba(255,215,0,0.85) 56%, rgba(255,215,0,0.85) 72%, transparent 72%)",
                        borderRadius: "50%"
                      } : {
                        background: "radial-gradient(circle, rgba(255,215,0,0.7) 28%, transparent 28%)",
                        borderRadius: "50%"
                      };
                    });
                    return h;
                  } catch { return {}; }
                })()}
                customBoardStyle={{ borderRadius: 8, boxShadow: "0 8px 40px rgba(0,0,0,0.5)" }}
                customLightSquareStyle={{ backgroundColor: "#f0d9b5" }}
                customDarkSquareStyle={{ backgroundColor: "#b58863" }}
              />
            </div>

            {(attempted || reviewMode) && (
              <div className="board-nav" style={{ marginTop: 8 }}>
                <span style={{ fontSize: 11, color: "var(--text-muted)", marginRight: 4 }}>Moves:</span>
                <button className="nav-btn"
                  onClick={() => reviewMode ? setReviewNavIdx(0) : setNavIdx(0)}
                  disabled={reviewMode ? reviewNavIdx === 0 : navIdx === 0}>⟪</button>
                <button className="nav-btn"
                  onClick={() => reviewMode ? setReviewNavIdx(i => Math.max(0, i-1)) : setNavIdx(i => Math.max(0, i-1))}
                  disabled={reviewMode ? reviewNavIdx === 0 : navIdx === 0}>‹</button>
                <span className="nav-info">
                  {reviewMode
                    ? `${reviewNavIdx + 1}/${reviewMoveFens.length}`
                    : `${navIdx + 1}/${allMoveFens.length}`}
                </span>
                <button className="nav-btn"
                  onClick={() => reviewMode
                    ? setReviewNavIdx(i => Math.min(reviewMoveFens.length - 1, i + 1))
                    : setNavIdx(i => Math.min(allMoveFens.length - 1, i + 1))}
                  disabled={reviewMode ? reviewNavIdx >= reviewMoveFens.length - 1 : navIdx >= allMoveFens.length - 1}>›</button>
                <button className="nav-btn"
                  onClick={() => reviewMode ? setReviewNavIdx(reviewMoveFens.length - 1) : setNavIdx(allMoveFens.length - 1)}
                  disabled={reviewMode ? reviewNavIdx >= reviewMoveFens.length - 1 : navIdx >= allMoveFens.length - 1}>⟫</button>
              </div>
            )}
          </div>

          <div className="solver-right">
            {reviewMode ? (
              <div className="review-panel">
                <h4>Review</h4>
                <div className={`review-result ${reviewAttempt?.correct ? "correct" : "wrong"}`}>
                  {reviewAttempt ? (reviewAttempt.correct ? "✓ You got this right!" : "✗ You got this wrong") : "Not attempted"}
                </div>
                <div className="review-moves">
                  <label>You played:</label>
                  <div className="move-chips">
                    {(reviewAttempt?.moves || []).map((m, i) => <span key={i} className="move-chip">{m.san}</span>)}
                    {!reviewAttempt && <span style={{ color: "var(--text-muted)", fontSize: 13 }}>—</span>}
                  </div>
                </div>
                <div className="review-moves">
                  <label>Correct answer:</label>
                  <div className="move-chips">
                    {puzzle.solution.map((m, i) => <span key={i} className="move-chip correct-chip">{m.san}</span>)}
                  </div>
                </div>
                <div className="review-nav">
                  <button className="btn btn-secondary" disabled={reviewPuzzleIdx === 0}
                    onClick={() => { const i = reviewPuzzleIdx - 1; setReviewPuzzleIdx(i); setReviewNavIdx(0); loadReviewPuzzle(activeSet, i, null); }}>← Prev</button>
                  <button className="btn btn-secondary" disabled={reviewPuzzleIdx >= activeSet.totalPuzzles - 1}
                    onClick={() => { const i = reviewPuzzleIdx + 1; setReviewPuzzleIdx(i); setReviewNavIdx(0); loadReviewPuzzle(activeSet, i, null); }}>Next →</button>
                </div>
              </div>
            ) : (
              <div className="attempt-panel">
                {!attempted ? (
                  <div className="attempt-hint">
                    <div className="hint-icon">🧩</div>
                    <p>Find the best move{puzzle.solution.length > 1 ? "s" : ""}!</p>
                    <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                      {puzzle.solution.filter((_, i) => i % 2 === 0).length} move{puzzle.solution.filter((_, i) => i % 2 === 0).length > 1 ? "s" : ""} to solve
                    </p>
                    <p style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 8 }}>
                      Click a piece to see where it can move
                    </p>
                  </div>
                ) : (
                  <div className={`result-feedback ${isCorrect ? "correct" : "wrong"}`}>
                    <div className="feedback-msg">{resultMsg}</div>
                    {!isCorrect && (
                      <div style={{ marginTop: 12 }}>
                        <div className="solution-display">
                          <label>Correct moves:</label>
                          <div className="move-chips">
                            {puzzle.solution.map((m, i) => <span key={i} className="move-chip correct-chip">{m.san}</span>)}
                          </div>
                        </div>
                      </div>
                    )}
                    <button className="btn btn-primary" style={{ width: "100%", marginTop: 16 }} onClick={nextPuzzle}>
                      {activePuzzleIdx + 1 >= activeSet.totalPuzzles ? "Finish Set" : "Solve Next →"}
                    </button>
                  </div>
                )}
                <div className="progress-bar-wrap">
                  <div className="progress-bar" style={{ width: `${(activePuzzleIdx / activeSet.totalPuzzles) * 100}%` }} />
                </div>
                <p style={{ fontSize: 11, color: "var(--text-muted)", textAlign: "center" }}>{activePuzzleIdx}/{activeSet.totalPuzzles} completed</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="student-puzzles">
      <h2 className="student-section-title">Puzzles</h2>
      {loading && <div className="loading-screen" style={{ minHeight: 200 }}><div className="spinner" /></div>}
      {!loading && puzzleSets.length === 0 && (
        <div className="student-empty"><div>🧩</div><h3>No puzzles yet</h3><p>Your teacher has not assigned any puzzles yet.</p></div>
      )}
      <div className="student-puzzle-grid">
        {puzzleSets.map(set => {
          const status = getSetStatus(set);
          const prog = myProgress[set.id] || {};
          const attCount = Object.keys(prog).length;
          const score = getSetScore(set);
          const stars = status === "complete" ? getStars(score, set.totalPuzzles) : null;
          return (
            <div key={set.id} className={`student-puzzle-card ${status}`} onClick={() => openSet(set)}>
              {status === "incomplete" && <div className="incomplete-badge">Incomplete</div>}
              <div className="spcard-icon">🧩</div>
              <h3 className="spcard-name">{set.name}</h3>
              <p className="spcard-count">{set.totalPuzzles} puzzle{set.totalPuzzles !== 1 ? "s" : ""}</p>
              <div className="spcard-progress">
                <div className="spcard-bar"><div className="spcard-bar-fill" style={{ width: `${(attCount / set.totalPuzzles) * 100}%` }} /></div>
                <span>{attCount}/{set.totalPuzzles} attempted</span>
              </div>
              {status === "complete" && stars !== null && <StarDisplay stars={stars} />}
              {status === "complete" && <p className="spcard-score">{score}/{set.totalPuzzles} correct</p>}
              <div className={`spcard-status-tag ${status}`}>
                {status === "new" && "Start"}{status === "incomplete" && "Continue"}{status === "complete" && "Review"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
